import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Injector, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Observable } from "rxjs";
import { FormValidationService } from "../form-validation.service";
import { VButtonStatus, VInputComponent } from "../v-input/v-input.component";
import "./v-autocomplete.component.scss";

@UntilDestroy()
@Component({
    selector: "v-autocomplete",
    template: require('./v-autocomplete.component.html')
})
export class VAutocompleteComponent<T> extends VInputComponent implements OnChanges {
    @Input() selectedItem: T;
    @Input() selectedItemId: string;
    @Input() selectedItemProp: string = "Id"; // The property on the item used to identify selected items

    @Input() items: T[];
    @Input() itemDisplayProp: string; // The property on the item to display e.g. if it is a Vehicle you would pass ShortReg

    @Input() itemPromise: Promise<T[]>;
    @Input() itemTemplate: TemplateRef<any>;
    @Input() loading = false;
    @Input() itemFilter: (item: T, text: string) => boolean;

    @Input() itemObservable$: Observable<T[]>;

    @Input() label: string;
    @Input() minLength = 0;

    @Input() disabled: boolean;

    @Input() useStatus: boolean = true;

    @Output() itemClicked = new EventEmitter<T>();

    @Output() noMatches = new EventEmitter<string | number>();

    @Output() clear = new EventEmitter<void>();

    @Output() textChanged = new EventEmitter<string>();

    @ViewChild("autoCompleteInput", { read: ElementRef }) autoCompleteInputRef: ElementRef<HTMLElement>;
    ignoreClicksFrom: ElementRef<HTMLElement>;

    // ---

    matches: T[] = [];

    inputStatus: VButtonStatus = VButtonStatus.Default;
    showDropdown = false;

    constructor(
        protected readonly injector: Injector,
        protected readonly formValidationService: FormValidationService,
        private readonly cd: ChangeDetectorRef
    ) {
        super(injector, formValidationService);
    }

    ngOnChanges(changes: SimpleChanges) {

        if (changes["itemPromise"]) {
            this.onLoadingItems();
        }

        if (changes["itemObservable$"]) {
            this.onLoadingItemsFromObservable();
        }

        if (changes["selectedItemId"] || changes["items"]) {
            this.setSelectedItemFromProps();
        }

        if (changes["items"]) {
            this.validateItemProps();
        }

        if (changes["disabled"]
            && changes["disabled"].previousValue === false
            && changes["disabled"].currentValue === true) {
            // Don't clear if initialised as disabled
            this.clearInput();
        }
    }

    ngAfterViewInit() {
        //Avoids ExpressionChangedAfterItHasBeenCheckedError
        this.ignoreClicksFrom = this.autoCompleteInputRef; 
        this.cd.detectChanges();
    }

    clearInput(): void {
        this.value = null;
        this.reset();
    }

    onTextChanged(text: string, filterResult = true, triggeredByInput: boolean = true) {
        // If no changes, then ignore this
        if (text == this.value) {
            return;
        }

        if (triggeredByInput) {
            this.textChanged.next(text);
        }

        this.value = text;

        this.inputStatus = VButtonStatus.Default;

        if (!this.itemPromise && !this.items && !this.itemObservable$) {
            console.warn("No Autocomplete itemPromise or itemObservable set");
            return;
        }

        // Reset selected item on typing
        if (triggeredByInput) {
            this.reset();
        }

        if (filterResult) {
            this.filterResults();
        }
    }

    onFocus() {
        this.focus.emit();
        this.filterResults();
        this.toggleDropdownVisibility(this.matches.length != 0);
    }

    onBlur() {
        this.blur.emit();
        this.updateStatus(true);

        // allow time for mouse click on item before v-input blur removes item DOM elements
        setTimeout(() => {
            this.toggleDropdownVisibility(false);
        }, 100);
    }

    onItemClicked(item: T) {
        if (!item) {
            console.warn("Null item selected");
            return;
        }

        this.onTextChanged(this.itemDisplayProp ? item[this.itemDisplayProp] : item, false, false);

        this.showDropdown = false;
        this.selectedItem = item;

        this.updateStatus();
        this.itemClicked.emit(item);
    }

    onEnterPressed() {
        if (this.matches.length) {
            this.onItemClicked(this.matches[0]);
        }
    }

    onClosed() {
        this.toggleDropdownVisibility();
    }

    // ---

    private reset(): void {
        this.selectedItem = null;
        this.selectedItemId = null;
        this.inputStatus = VButtonStatus.Default;
        this.clear.emit();
    }

    private filterResults() {

        this.matches = [];

        if (!this.items) {
            return;
        }

        if (!this.value) {
            this.matches = this.items;
            return;
        }

        if (!this.itemFilter) {
            console.error("No itemFilter specified");
            return;
        }

        const valueString = `${this.value}`;

        if (valueString.length < this.minLength) {
            return;
        }

        var lowerCaseText = valueString.toLowerCase();

        this.items.forEach(item => {
            if (this.itemFilter(item, lowerCaseText)) {
                this.matches.push(item);
            }
        });

        if (!this.matches.length) {
            this.noMatches.emit(this.value);
        }

        this.toggleDropdownVisibility();
    }

    private toggleDropdownVisibility(force?: boolean) {
        if (force != null) {
            this.showDropdown = force;
            return;
        }

        if (this.selectedItem) {
            this.showDropdown = false;
        }
        else {
            this.showDropdown = this.matches.length > 0;
        }
    }

    private onLoadingItems() {

        if (!this.itemPromise) {
            return;
        }

        this.loading = true;

        this.itemPromise
            .then(results => {
                this.items = results;
            })
            .finally(() => {
                this.loading = false;
                this.onDataLoaded();
            });
    }

    private onLoadingItemsFromObservable(): void {
        if (!this.itemObservable$) {
            return;
        }

        this.loading = true;

        this.itemObservable$.pipe(untilDestroyed(this)).subscribe(data => {
            this.items = data;
            this.onDataLoaded(false);
            this.loading = false;
        })
    }

    private onDataLoaded(openDropdown = true): void {
        this.filterResults();
        this.toggleDropdownVisibility(openDropdown);
        this.setSelectedItemFromProps();
    }

    private updateStatus(allowError = false) {

        if (!this.useStatus) {
            return;
        }

        this.status = VButtonStatus.Default;

        if (this.selectedItem) {
            this.inputStatus = VButtonStatus.Success;
        }
        else if (allowError && this.value && `${this.value}`.length > this.minLength) {
            this.inputStatus = VButtonStatus.Error;
        }
    }

    private setSelectedItemFromProps() {
        if (this.selectedItem || !this.selectedItemId || !this.items) {
            return;
        }

        if (this.itemPromise && this.loading) {
            this.itemPromise.then(() => {
                this.setSelectedItemFromProps();
            });

            return;
        }

        var selectedItem = this.items.find(i => i[this.selectedItemProp] == this.selectedItemId);
        this.onItemClicked(selectedItem);
    }

    private validateItemProps(): void {
        if (!this.selectedItemProp || !this.items) {
            return;
        }

        const anyItemWithoutProp = this.items.find(item => !item[this.selectedItemProp] && item[this.selectedItemProp] !== 0);

        if (anyItemWithoutProp) {
            console.warn("AutoComplete.Items issue: selectedItemProp set to", this.selectedItemProp, "but item data has no matching property");
        }
    }
}
